/**
 * Route registry for the two organization consoles (ROADMAP §0 UX pass item 3).
 *
 * The district console gives every section its own route; the org consoles kept
 * theirs in local tab state, so a section could not be linked to, bookmarked, or
 * reached with the back button. These helpers make the URL the single source of
 * truth: a page derives its active section from `location.pathname` and the tab
 * bar only navigates.
 *
 * Adding a section is one entry in the relevant list — the tab bar, the sidebar,
 * the breadcrumb trail and (for the super-admin console) the `<Route>` in
 * App.tsx all read from it.
 *
 * Pure module: no React, no Firebase, so it is unit-tested in
 * `tests/unit/orgConsoleNav.test.ts`.
 */

export interface OrgConsoleSection {
  /** Path slug under the console base. Also the MUI `Tabs` value. */
  key: string;
  /**
   * Tab label, and the breadcrumb leaf unless `crumbs` says otherwise.
   * English, and the test's source of truth: it is also the `defaultValue`
   * passed to `t(section.labelKey, { defaultValue: section.label })`.
   */
  label: string;
  /** i18next key (with its `console:` namespace prefix) that resolves `label`. */
  labelKey: string;
  /** Sidebar entry label, when it differs from the tab label. */
  navLabel?: string;
  /** i18next key resolving `navLabel`. Present exactly when `navLabel` is. */
  navLabelKey?: string;
  /** Older slugs and `?tab=` values that still resolve to this section. */
  aliases?: string[];
  /** Breadcrumb trail after the organization name. Defaults to `[label]`. */
  crumbs?: string[];
  /** i18next keys resolving `crumbs`, in the same order. Defaults to `[labelKey]`. */
  crumbKeys?: string[];
  /** Tab bar this section belongs to. Sections in different groups never share one. */
  group?: 'main' | 'settings';
  /** Listed in the sidebar's console section. */
  inNav?: boolean;
  /** Premium-only (message monitoring, custom branding). */
  premium?: boolean;
  /** Only rendered for users who can manage the organization. */
  manageOnly?: boolean;
}

/** Base path of the school-admin console (`OrganizationAdmin`). */
export const ORG_ADMIN_BASE = '/org-admin';

/** Base path of the super-admin console for one organization (`OrganizationDetails`). */
export const orgDetailBase = (organizationId: string): string =>
  `/organization/${organizationId}`;

/**
 * School-admin console sections. `main` sections share the top tab bar;
 * `settings` sections share the Settings tab bar; monitoring is a full page of
 * its own, reached from the sidebar.
 */
export const ORG_ADMIN_SECTIONS: OrgConsoleSection[] = [
  {
    key: 'overview',
    label: 'Overview',
    labelKey: 'console:sections.orgAdmin.overview',
    group: 'main',
    inNav: true,
    aliases: ['details'],
  },
  {
    key: 'members',
    label: 'Members',
    labelKey: 'console:sections.orgAdmin.members',
    group: 'main',
    inNav: true,
    aliases: ['admins'],
  },
  {
    key: 'invitations',
    label: 'Invitations',
    labelKey: 'console:sections.orgAdmin.invitations',
    group: 'main',
    inNav: true,
  },
  {
    key: 'teams',
    label: 'Teams',
    labelKey: 'console:sections.orgAdmin.teams',
    group: 'main',
    inNav: true,
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    labelKey: 'console:sections.orgAdmin.monitoring',
    inNav: true,
    premium: true,
  },
  // The activity feed (NEXT_QUEUE_SPEC §5.10): a page of its own, like monitoring.
  {
    key: 'activity',
    label: 'Activity',
    labelKey: 'console:sections.orgAdmin.activity',
    inNav: true,
  },
  {
    key: 'settings',
    label: 'Details',
    labelKey: 'console:sections.orgAdmin.settingsDetails',
    navLabel: 'Settings',
    navLabelKey: 'console:sections.orgAdmin.settingsNav',
    group: 'settings',
    inNav: true,
    crumbs: ['Settings', 'Details'],
    crumbKeys: ['console:sections.orgAdmin.settingsNav', 'console:sections.orgAdmin.settingsDetails'],
  },
  {
    key: 'settings/billing',
    label: 'Billing',
    labelKey: 'console:sections.orgAdmin.billing',
    group: 'settings',
    crumbs: ['Settings', 'Billing'],
    crumbKeys: ['console:sections.orgAdmin.settingsNav', 'console:sections.orgAdmin.billing'],
  },
  {
    key: 'settings/branding',
    label: 'Branding',
    labelKey: 'console:sections.orgAdmin.branding',
    group: 'settings',
    premium: true,
    crumbs: ['Settings', 'Branding'],
    crumbKeys: ['console:sections.orgAdmin.settingsNav', 'console:sections.orgAdmin.branding'],
  },
];

/** Super-admin organization console sections, in tab order. */
export const ORG_DETAIL_SECTIONS: OrgConsoleSection[] = [
  { key: 'overview', label: 'Overview', labelKey: 'console:sections.orgDetail.overview', group: 'main' },
  {
    key: 'members',
    label: 'Members',
    labelKey: 'console:sections.orgDetail.members',
    group: 'main',
    manageOnly: true,
  },
  {
    key: 'permissions',
    label: 'Permissions',
    labelKey: 'console:sections.orgDetail.permissions',
    group: 'main',
    manageOnly: true,
  },
  {
    key: 'teams',
    label: 'Teams',
    labelKey: 'console:sections.orgDetail.teams',
    group: 'main',
    manageOnly: true,
  },
  {
    key: 'billing',
    label: 'Billing',
    labelKey: 'console:sections.orgDetail.billing',
    group: 'main',
    manageOnly: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    labelKey: 'console:sections.orgDetail.analytics',
    group: 'main',
    manageOnly: true,
  },
  { key: 'schedule', label: 'Schedule', labelKey: 'console:sections.orgDetail.schedule', group: 'main' },
];

/** Breadcrumb root label for each console. */
export const ORG_ADMIN_ROOT_CRUMB = 'Organization admin';
export const ORG_ADMIN_ROOT_CRUMB_KEY = 'console:sections.orgAdmin.root';
export const ORG_DETAIL_ROOT_CRUMB = 'Organizations';
export const ORG_DETAIL_ROOT_CRUMB_KEY = 'console:sections.orgDetail.root';

/** The path a section lives at, e.g. `/org-admin/members`. */
export function orgSectionPath(base: string, key: string): string {
  return `${trimSlashes(base)}/${key}`;
}

/**
 * The slug a pathname points at inside `base`: `''` for the base itself,
 * `null` when the pathname is not inside this console at all.
 */
export function orgSectionSlug(pathname: string, base: string): string | null {
  const cleanBase = trimSlashes(base);
  const cleanPath = trimSlashes(pathname);
  if (cleanPath === cleanBase) return '';
  if (cleanPath.startsWith(`${cleanBase}/`)) return cleanPath.slice(cleanBase.length + 1);
  return null;
}

/**
 * The section a console pathname shows: the base resolves to the first section,
 * legacy slugs resolve through `aliases`, and an unknown slug yields `null` so
 * the caller can redirect.
 */
export function orgSectionFromPath(
  pathname: string,
  base: string,
  sections: OrgConsoleSection[],
): OrgConsoleSection | null {
  const slug = orgSectionSlug(pathname, base);
  if (slug === null) return null;
  if (slug === '') return sections[0] ?? null;
  return sections.find((s) => s.key === slug || s.aliases?.includes(slug)) ?? null;
}

/** The section an old `?tab=<name>` link meant, or `null` if it names nothing. */
export function orgSectionFromTab(
  tab: string | null | undefined,
  sections: OrgConsoleSection[],
): OrgConsoleSection | null {
  if (!tab) return null;
  const wanted = tab.trim().toLowerCase();
  if (!wanted) return null;
  return (
    sections.find(
      (s) =>
        s.key.toLowerCase() === wanted ||
        s.label.toLowerCase() === wanted ||
        s.aliases?.some((a) => a.toLowerCase() === wanted),
    ) ?? null
  );
}

/**
 * Where a console URL should really be, or `null` when it is already canonical.
 *
 * Handles the three legacy shapes in one place: `?tab=<name>` query links, the
 * old path aliases (`/org-admin/details`), and the bare console base. Any other
 * search params are preserved so deep links like `?event=` keep working.
 */
export function orgCanonicalRedirect(
  pathname: string,
  search: string,
  base: string,
  sections: OrgConsoleSection[],
): string | null {
  const fallback = sections[0];
  if (!fallback) return null;

  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  if (tab !== null) {
    const target = orgSectionFromTab(tab, sections) ?? fallback;
    params.delete('tab');
    return withSearch(orgSectionPath(base, target.key), `?${params.toString()}`);
  }

  const slug = orgSectionSlug(pathname, base);
  if (slug === null) return null;
  if (slug === '') return withSearch(orgSectionPath(base, fallback.key), search);

  const section = sections.find((s) => s.key === slug || s.aliases?.includes(slug));
  if (!section) return withSearch(orgSectionPath(base, fallback.key), search);
  if (section.key === slug) return null;
  return withSearch(orgSectionPath(base, section.key), search);
}

/** The breadcrumb trail after the organization name. */
export function orgSectionCrumbs(section: OrgConsoleSection): string[] {
  return section.crumbs ?? [section.label];
}

interface SectionFilter {
  hasPremium?: boolean;
  canManage?: boolean;
}

/** Sections in one tab bar that the current user may see, in registry order. */
export function orgSectionsInGroup(
  sections: OrgConsoleSection[],
  group: 'main' | 'settings',
  filter: SectionFilter = {},
): OrgConsoleSection[] {
  return sections.filter((s) => s.group === group && isVisible(s, filter));
}

/** Sidebar entries for a console section, in registry order. */
export function orgNavSections(
  sections: OrgConsoleSection[],
  filter: SectionFilter = {},
): OrgConsoleSection[] {
  return sections.filter((s) => s.inNav && isVisible(s, filter));
}

/** The label a section uses in the sidebar. */
export function orgSectionNavLabel(section: OrgConsoleSection): string {
  return section.navLabel ?? section.label;
}

/** The i18next key for `orgSectionNavLabel()`. */
export function orgSectionNavLabelKey(section: OrgConsoleSection): string {
  return section.navLabelKey ?? section.labelKey;
}

/** The i18next keys for `orgSectionCrumbs()`, in the same order. */
export function orgSectionCrumbKeys(section: OrgConsoleSection): string[] {
  return section.crumbKeys ?? [section.labelKey];
}

function isVisible(section: OrgConsoleSection, filter: SectionFilter): boolean {
  if (section.premium && filter.hasPremium === false) return false;
  if (section.manageOnly && filter.canManage === false) return false;
  return true;
}

function trimSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

function withSearch(path: string, search: string): string {
  const clean = (search || '').replace(/^\?/, '');
  return clean ? `${path}?${clean}` : path;
}
